/**
 * escalation-queue-page.tsx — Escalation queue list view for staff:rd and staff:md.
 *
 * Story 8.2: Task 7.1
 *
 * AC 4: Shows entries sorted by severity (emergency pinned), then SLA remaining.
 * AC 6: Multiple open records for same member shown as separate rows (not collapsed).
 * AC 7: Emergency entries get red-border indicator + browser Notification on mount/poll.
 *
 * Polling: 30s interval. Emergency notification fires on each poll cycle if emergency entries exist.
 */

import { usePortalAuth } from '@nrx/auth-ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

// @nrx/auth-ui is not built in worktrees — cast to known shape to avoid no-unsafe-* errors.
interface PortalAuthResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: { access_token: string; profile: { sub: string } } | null;
}

import type { EscalationQueueEntry } from './escalation-api.js';
import { escalationApi } from './escalation-api.js';

const POLL_INTERVAL_MS = 30_000;

function formatTimeAgo(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffSeconds = Math.floor((now - then) / 1000);
  if (diffSeconds < 60) return `${String(diffSeconds)}s ago`;
  if (diffSeconds < 3600) return `${String(Math.floor(diffSeconds / 60))}m ago`;
  return `${String(Math.floor(diffSeconds / 3600))}h ago`;
}

function formatSlaRemaining(seconds: number): string {
  if (seconds < 0) return 'SLA BREACHED';
  if (seconds < 3600) return `${String(Math.floor(seconds / 60))}m remaining`;
  return `${String(Math.floor(seconds / 3600))}h remaining`;
}

const SEVERITY_BADGE_CLASSES: Record<EscalationQueueEntry['severity'], string> = {
  low: 'bg-blue-100 text-blue-700',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-orange-100 text-orange-700',
  emergency: 'bg-red-100 text-red-700 font-bold',
};

function requestBrowserNotification(count: number): void {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission === 'granted') {
    new Notification('Betty — Emergency Escalation', {
      body: `${String(count)} emergency escalation${count > 1 ? 's' : ''} require immediate attention.`,
      icon: '/favicon.ico',
    });
  } else if (Notification.permission !== 'denied') {
    void Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        requestBrowserNotification(count);
      }
    });
  }
}

export function EscalationQueuePage(): React.ReactElement {
  const auth = usePortalAuth() as PortalAuthResult;
  const token = auth.user?.access_token;

  const [entries, setEntries] = useState<EscalationQueueEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const hasFiredNotification = useRef(false);

  const fetchEntries = useCallback(async (): Promise<void> => {
    try {
      const result = await escalationApi.listEscalations({ status: 'open', limit: 50 }, token);
      setEntries(result.items);
      setTotalCount(result.totalCount);
      setError(null);

      // AC 7: trigger browser notification for emergency entries
      const emergencyCount = result.items.filter((e) => e.severity === 'emergency').length;
      if (emergencyCount > 0) {
        requestBrowserNotification(emergencyCount);
        hasFiredNotification.current = true;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load escalation queue.');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void fetchEntries();
    const interval = setInterval(() => {
      void fetchEntries();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchEntries]);

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-gray-500">Loading escalation queue…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded bg-red-50 p-4 text-red-700" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Escalation Queue</h1>
        <span className="text-sm text-gray-500">{totalCount} total</span>
      </div>

      {entries.length === 0 ? (
        <p className="text-gray-500">No open escalations.</p>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => (
            <Link
              key={entry.id}
              to={`/escalation-queue/${entry.id}`}
              className={[
                'block rounded-lg border p-4 transition-colors hover:bg-gray-50',
                entry.severity === 'emergency'
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-200 bg-white',
              ].join(' ')}
              data-testid={`escalation-row-${entry.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* AC 7: visual pin indicator for emergency */}
                  {entry.severity === 'emergency' && (
                    <span
                      className="mb-1 inline-block rounded bg-red-600 px-2 py-0.5 text-xs font-bold text-white"
                      data-testid="emergency-pin"
                    >
                      ⚠ EMERGENCY — PINNED
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {entry.memberName ?? 'Member context loading…'}
                    </span>
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${SEVERITY_BADGE_CLASSES[entry.severity]}`}
                      data-testid={`severity-badge-${entry.severity}`}
                    >
                      {entry.severity.toUpperCase()}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    {entry.triggerReason ?? `${entry.triggerType} trigger`}
                  </p>
                  {entry.conversationSnippet && (
                    <p className="mt-1 line-clamp-2 text-xs text-gray-400 italic">
                      &ldquo;{entry.conversationSnippet}&rdquo;
                    </p>
                  )}
                </div>
                <div className="ml-4 text-right text-sm text-gray-500">
                  <p>{formatTimeAgo(entry.triggerTimestamp)}</p>
                  <p
                    className={
                      entry.slaRemainingSeconds < 0
                        ? 'font-semibold text-red-600'
                        : entry.slaRemainingSeconds < 3600
                          ? 'text-orange-500'
                          : 'text-gray-500'
                    }
                  >
                    {formatSlaRemaining(entry.slaRemainingSeconds)}
                  </p>
                  {entry.claimedBy && (
                    <p className="text-xs text-blue-600">Claimed by {entry.claimedBy.staffRole}</p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
